import React, { useEffect } from 'react';
import { C } from '../utils/styles';
import HealthCuesLogo from './HealthCuesLogo';

// ---------------------------------------------------------------------------
// MobileNavDrawer
// ---------------------------------------------------------------------------
// Right-side slide-in drawer with the full primary navigation. Used on
// viewports <= 720px (see useIsMobile). Each tab button is large enough
// to satisfy Apple's 44pt tap-target HIG and uses -webkit-tap-highlight-color
// so taps register cleanly on iOS Safari.
// ---------------------------------------------------------------------------

const Z = {
  backdrop: 1000,
  drawer: 1001,
};

export default function MobileNavDrawer({
  open,
  onClose,
  tabs,            // [{ id, label }]
  activeTab,
  onSelect,        // (tabId) => void
  isHardBlocked,
  unreadTotal,
  currentUser,
}) {
  // Lock body scroll while open
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Escape to close
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const userDisplay = currentUser
    ? (currentUser.firstName ? `${currentUser.firstName} ${currentUser.lastName || ''}`.trim() : (currentUser.email || ''))
    : '';
  const userRoleLabel = currentUser
    ? (currentUser.role === 'super_user' ? 'Super User'
        : currentUser.role === 'account_manager' ? 'Account Manager'
        : currentUser.role === 'employer_contact' ? 'Employer'
        : currentUser.role === 'employee' ? 'Employee'
        : currentUser.role)
    : '';

  return (
    <>
      {/* Backdrop */}
      <div
        data-testid="mobile-nav-backdrop"
        onClick={onClose}
        aria-hidden="true"
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15, 23, 42, 0.45)',
          backdropFilter: 'blur(2px)',
          WebkitBackdropFilter: 'blur(2px)',
          opacity: open ? 1 : 0,
          pointerEvents: open ? 'auto' : 'none',
          transition: 'opacity 200ms ease',
          zIndex: Z.backdrop,
        }}
      />

      {/* Drawer */}
      <aside
        data-testid="mobile-nav-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Primary navigation"
        aria-hidden={!open}
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          height: '100dvh',
          maxHeight: '100vh',
          width: 'min(86vw, 320px)',
          background: '#fff',
          boxShadow: '-12px 0 36px rgba(15, 23, 42, 0.18)',
          transform: open ? 'translateX(0)' : 'translateX(100%)',
          transition: open
            ? 'transform 240ms cubic-bezier(0.22, 1, 0.36, 1), visibility 0s linear 0s'
            : 'transform 240ms cubic-bezier(0.22, 1, 0.36, 1), visibility 0s linear 240ms',
          zIndex: Z.drawer,
          display: 'flex',
          flexDirection: 'column',
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
          // Critical for iOS Safari: ensure the drawer cannot intercept
          // any touch events while it's translated off-screen, otherwise
          // tapping anywhere on the right edge (incl. the burger) can land
          // on this element due to sub-pixel hit-test quirks.
          pointerEvents: open ? 'auto' : 'none',
          visibility: open ? 'visible' : 'hidden',
          WebkitTransform: open ? 'translateX(0)' : 'translateX(100%)',
        }}
      >
        {/* Drawer header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 18px',
          borderBottom: `1px solid ${C.border}`,
          gap: 12,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <HealthCuesLogo size={30} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: C.text, letterSpacing: '-0.01em' }}>HealthCues</div>
              <div style={{ fontSize: 11, color: C.textLight, marginTop: 1 }}>Client Success Engine</div>
            </div>
          </div>
          <button
            type="button"
            data-testid="mobile-nav-close"
            onClick={onClose}
            aria-label="Close navigation"
            style={{
              width: 40,
              height: 40,
              borderRadius: 8,
              border: `1px solid ${C.border}`,
              background: '#fff',
              color: C.textMid,
              fontSize: 20,
              fontWeight: 700,
              cursor: 'pointer',
              WebkitTapHighlightColor: 'rgba(0,0,0,0.05)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              padding: 0,
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        {/* User context strip */}
        {currentUser && (
          <div style={{
            padding: '12px 18px',
            background: C.bg,
            borderBottom: `1px solid ${C.border}`,
          }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.textLight, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Signed in
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginTop: 2 }}>
              {userDisplay || '—'}
            </div>
            {userRoleLabel && (
              <div style={{ fontSize: 11, color: C.textMid, marginTop: 1 }}>{userRoleLabel}</div>
            )}
          </div>
        )}

        {/* Tab list */}
        <nav style={{ flex: 1, overflowY: 'auto', padding: '8px 10px 18px' }}>
          {tabs.map((t) => {
            const isActive = activeTab === t.id;
            const tabDisabled = isHardBlocked && t.id !== 'myTraining';
            const showBadge = t.id === 'clients' && unreadTotal > 0;
            return (
              <button
                key={t.id}
                type="button"
                data-testid={`mobile-nav-tab-${t.id}`}
                onClick={() => {
                  if (tabDisabled) return;
                  onSelect(t.id);
                  onClose();
                }}
                disabled={tabDisabled}
                title={tabDisabled ? 'Complete required training to unlock' : undefined}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  minHeight: 48,
                  padding: '12px 14px',
                  marginTop: 4,
                  borderRadius: 10,
                  border: 'none',
                  background: isActive ? C.accent : 'transparent',
                  color: isActive ? '#fff' : (tabDisabled ? C.textLight : C.text),
                  fontSize: 15,
                  fontWeight: 700,
                  textAlign: 'left',
                  cursor: tabDisabled ? 'not-allowed' : 'pointer',
                  opacity: tabDisabled ? 0.5 : 1,
                  WebkitTapHighlightColor: 'rgba(15, 23, 42, 0.08)',
                  transition: 'background 0.15s, color 0.15s',
                }}
              >
                <span>{t.label}</span>
                {showBadge && (
                  <span
                    data-testid="mobile-clients-unread-badge"
                    style={{
                      minWidth: 22,
                      height: 22,
                      padding: '0 7px',
                      borderRadius: 11,
                      background: isActive ? '#fff' : '#dc2626',
                      color: isActive ? C.accent : '#fff',
                      fontSize: 11,
                      fontWeight: 800,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {unreadTotal > 9 ? '9+' : unreadTotal}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        {/* Footer copy */}
        <div style={{ padding: '12px 18px', borderTop: `1px solid ${C.border}`, fontSize: 10, color: C.textLight, textAlign: 'center', letterSpacing: '0.04em' }}>
          © 2026 HealthCues · Client Success Engine
        </div>
      </aside>
    </>
  );
}
